import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Save, RotateCcw, Eye, Trash2, Plus, X, Home } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useContent } from "../../context/ContentContext";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import HeroEditor from "./editors/HeroEditor";
import AboutEditor from "./editors/AboutEditor";
import CardsEditor from "./editors/CardsEditor";
import BrandsEditor from "./editors/BrandsEditor";
import FooterEditor from "./editors/FooterEditor";
import SubpageEditor from "./editors/SubpageEditor";
import ExperienciasEditor from "./editors/ExperienciasEditor";
import MessagesPanel from "./editors/MessagesPanel";

const TABS = [
  { id: "hero", label: "Hero" },
  { id: "about", label: "Sobre" },
  { id: "cards", label: "Tarjetas" },
  { id: "brands", label: "Marcas" },
  { id: "footer", label: "Footer" },
  { id: "music_page", label: "Página Music" },
  { id: "teams_page", label: "Página Teams" },
  { id: "experiencias_page", label: "Experiencias" },
  { id: "messages", label: "Mensajes" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { content, refresh, setContent } = useContent();
  const { toast } = useToast();
  const nav = useNavigate();
  const [active, setActive] = useState("hero");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (content) setDraft(structuredClone(content)); }, [content]);

  const patch = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = { ...draft };
      delete payload.id;
      delete payload.updated_at;
      const { data } = await api.put("/content", payload);
      setContent(data);
      setDraft(structuredClone(data));
      toast({ title: "Guardado", description: "Los cambios se aplicaron correctamente." });
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "No se pudo guardar." });
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("¿Restaurar contenido a los valores por defecto? Esta acción no se puede deshacer.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/content/reset");
      setContent(data);
      setDraft(structuredClone(data));
      toast({ title: "Restaurado", description: "Contenido restablecido." });
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "No se pudo restaurar." });
    } finally { setSaving(false); }
  };

  const doLogout = () => { logout(); nav("/admin/login"); };

  if (!draft) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white/60 font-mono-caps tracking-widest">CARGANDO...</div>;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="relative w-9 h-9 rounded-full flex items-center justify-center overflow-hidden">
              <span className="absolute inset-0 bg-gradient-to-br from-[#ff8a3c] via-[#ff6a2b] to-[#2ec9a8]" />
              <span className="relative text-black font-black text-lg font-display">K</span>
            </span>
            <div>
              <div className="font-heading text-lg tracking-[0.15em] leading-none">KILOMBO</div>
              <div className="font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50">CMS · {user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 text-white/70 hover:text-white text-sm transition-colors">
              <Eye className="w-4 h-4" /> Ver sitio
            </Link>
            <button onClick={reset} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 text-white/70 hover:text-white text-sm transition-colors">
              <RotateCcw className="w-4 h-4" /> Restaurar
            </button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#ff6a2b] hover:bg-[#ff8a3c] text-black font-medium text-sm transition-colors disabled:opacity-60">
              <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
            </button>
            <button onClick={doLogout} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-red-500/40 text-white/70 hover:text-red-400 text-sm transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Layout */}
      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="rounded-xl border border-white/10 bg-neutral-950 p-2">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setActive(t.id)}
                className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-colors mb-1 ${
                  active === t.id ? "bg-white text-black font-medium" : "text-white/70 hover:text-white hover:bg-white/5"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0">
          <div className="rounded-xl border border-white/10 bg-neutral-950 p-6 md:p-8">
            {active === "hero" && <HeroEditor value={draft.hero} onChange={(v) => patch("hero", v)} />}
            {active === "about" && <AboutEditor value={draft.about} onChange={(v) => patch("about", v)} />}
            {active === "cards" && <CardsEditor value={draft.cards} onChange={(v) => patch("cards", v)} />}
            {active === "brands" && <BrandsEditor value={draft.brands} onChange={(v) => patch("brands", v)} />}
            {active === "footer" && <FooterEditor value={draft.footer} onChange={(v) => patch("footer", v)} />}
            {active === "music_page" && <SubpageEditor value={draft.music_page} onChange={(v) => patch("music_page", v)} accent="#ff6a2b" title="Página KILOMBO Music" />}
            {active === "teams_page" && <SubpageEditor value={draft.teams_page} onChange={(v) => patch("teams_page", v)} accent="#2ec9a8" title="Página KILOMBO Teams" />}
            {active === "experiencias_page" && <ExperienciasEditor value={draft.experiencias_page} onChange={(v) => patch("experiencias_page", v)} />}
            {active === "messages" && <MessagesPanel />}
          </div>
        </main>
      </div>
    </div>
  );
}
